import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Instagram, Send, MessageCircle } from "lucide-react";

const MAP_EMBED = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3720.5!2d72.85131747!3d21.21013815!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjHCsDEyJzM2LjUiTiA3MsKwNTEnMDQuNyJF!5e0!3m2!1sen!2sin!4v1700000000000";

export default function Contact() {
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ name: "", phone: "", email: "", loan_type: "Personal", amount: 100000, message: "" });
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get("/settings").then(({data}) => setSettings(data)); }, []);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", { ...form, amount: +form.amount });
      toast.success("Thanks! We'll get back to you soon.");
      setForm({ name: "", phone: "", email: "", loan_type: "Personal", amount: 100000, message: "" });
    } catch (err) { toast.error(formatErr(err)); } finally { setSending(false); }
  };
  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="sk-badge sk-badge-gold">Get in touch</div>
          <h1 className="mt-4 font-display text-5xl text-white leading-tight">Let's <span className="gold italic">talk finance</span></h1>
          <p className="mt-4 text-white/60 max-w-md">Speak with a SK Group loan advisor — free, no-obligation consultation.</p>
          <div className="mt-10 space-y-5">
            <Row icon={Phone} label="Call us" value={settings.contact_phone} href={`tel:${(settings.contact_phone||'').replace(/\s+/g,'')}`}/>
            <Row icon={MessageCircle} label="WhatsApp" value="Chat instantly" href={`https://wa.me/${(settings.whatsapp_number||'').replace(/\D/g,'')}`}/>
            <Row icon={Mail} label="Email" value={settings.contact_email} href={`mailto:${settings.contact_email}`}/>
            <Row icon={MapPin} label="Office" value={settings.address}/>
            <Row icon={Instagram} label="Instagram" value="@sk_group_finance_" href={settings.instagram_url}/>
          </div>

          <div className="mt-10 rounded-xl overflow-hidden border border-white/10">
            <iframe
              title="SK Group location"
              data-testid="google-map"
              src={MAP_EMBED}
              width="100%" height="240" style={{ border: 0 }}
              loading="lazy" referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
        <div className="lg:col-span-7">
          <form onSubmit={submit} className="sk-card-dark p-8 grid sm:grid-cols-2 gap-5">
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Name</div>
              <input data-testid="contact-name" required className="sk-input" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/>
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Phone</div>
              <input data-testid="contact-phone" required className="sk-input" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})}/>
            </label>
            <label className="block sm:col-span-2">
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Email</div>
              <input data-testid="contact-email" type="email" className="sk-input" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}/>
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Loan interest</div>
              <select data-testid="contact-loan-type" className="sk-input" value={form.loan_type} onChange={(e)=>setForm({...form,loan_type:e.target.value})}>
                {["Personal","Business","Home","Car","Gold","Education","Property","Machinery","Mortgage"].map(t=><option key={t}>{t}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Amount (₹)</div>
              <input data-testid="contact-amount" type="number" className="sk-input" value={form.amount} onChange={(e)=>setForm({...form,amount:e.target.value})}/>
            </label>
            <label className="block sm:col-span-2">
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Message</div>
              <textarea data-testid="contact-message" rows="4" className="sk-input" value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})}/>
            </label>
            <div className="sm:col-span-2">
              <button data-testid="contact-submit" disabled={sending} className="sk-btn-gold inline-flex items-center gap-2">
                <Send size={16}/> {sending ? "Sending..." : "Send enquiry"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
function Row({ icon: Icon, label, value, href }) {
  const Comp = href ? "a" : "div";
  return (
    <Comp href={href} target={href && href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="flex items-start gap-4 group">
      <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/25 flex items-center justify-center">
        <Icon size={16} className="text-[#D4AF37]"/>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-white/40">{label}</div>
        <div className="text-white group-hover:text-[#D4AF37] transition-colors">{value || "—"}</div>
      </div>
    </Comp>
  );
}
